import React, { useMemo } from "react";
import { CogIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { GPUInstanceType } from "types/InstanceTypes";
import { classNames } from "components/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/Graphs/Popover";
import { Switch } from "components/Graphs/Switch";
import * as Tooltip from "@radix-ui/react-tooltip";
import FilterCard, { FilterRemoveButton } from "./FilterCard";

interface CapabilitiesFilterProps {
  persistentOnly: boolean;
  setPersistentOnly: (persistentOnly: boolean) => void;
  flexiblePortControlOnly: boolean;
  setFlexiblePortControlOnly: (flexiblePortControlOnly: boolean) => void;
  flexibleStorageOnly: boolean;
  setFlexibleStorageOnly: (flexibleStorageOnly: boolean) => void;
  allGPUInstances?: GPUInstanceType[]; // Filtered instances (after other filters, before capability filters) for showing accurate counts
}

type FilterMode = "full" | "all" | "custom";

interface CapabilitiesFilterItemProps {
  label: string;
  description: string;
  tooltipText: string;
  count: number;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}

const CapabilitiesFilterItem: React.FC<CapabilitiesFilterItemProps> = ({
  label,
  description,
  tooltipText,
  count,
  checked,
  onCheckedChange,
}) => {
  return (
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <label className="text-sm font-medium text-white">{label}</label>
          <Tooltip.Provider>
            <Tooltip.Root delayDuration={0}>
              <Tooltip.Trigger asChild>
                <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-help" />
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  side="top"
                  className="z-50 max-w-xs rounded-md bg-black px-3 py-2 text-xs text-white shadow-md"
                >
                  {tooltipText}
                  <Tooltip.Arrow className="fill-black" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          </Tooltip.Provider>
          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-zinc-800 text-gray-400">
            {count}
          </span>
        </div>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
      <Switch
        aria-label={label}
        checked={checked}
        onCheckedChange={onCheckedChange}
      />
    </div>
  );
};

interface QuickShortcutButtonProps {
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
}

const QuickShortcutButton: React.FC<QuickShortcutButtonProps> = ({
  label,
  count,
  isActive,
  onClick,
}) => {
  return (
    <button
      onClick={onClick}
      className={classNames(
        "w-full flex items-center justify-between px-4 py-3 rounded-md text-sm font-medium transition-all duration-200",
        isActive
          ? "bg-highlight text-black shadow-md"
          : "bg-zinc-800 text-secondary hover:bg-zinc-700 hover:text-white",
      )}
    >
      <div className="flex items-center gap-2">
        {isActive && <CheckCircleIcon className="w-5 h-5 text-lime-700" />}
        <span>{label}</span>
      </div>
      <span
        className={classNames(
          "px-2 py-0.5 rounded-full text-xs font-semibold",
          isActive ? "bg-lime-600 text-black" : "bg-zinc-900 text-secondary",
        )}
      >
        {count}
      </span>
    </button>
  );
};

const CapabilitiesFilter: React.FC<CapabilitiesFilterProps> = ({
  persistentOnly,
  setPersistentOnly,
  flexiblePortControlOnly,
  setFlexiblePortControlOnly,
  flexibleStorageOnly,
  setFlexibleStorageOnly,
  allGPUInstances = [],
}) => {
  // Determine current mode
  const currentMode: FilterMode = useMemo(() => {
    if (persistentOnly && flexiblePortControlOnly && flexibleStorageOnly)
      return "full";
    if (!persistentOnly && !flexiblePortControlOnly && !flexibleStorageOnly)
      return "all";
    return "custom";
  }, [persistentOnly, flexiblePortControlOnly, flexibleStorageOnly]);

  // Calculate instance counts for each mode
  const counts = useMemo(() => {
    return {
      full: allGPUInstances.filter(
        (i) =>
          i.stoppable === true &&
          i.can_modify_firewall_rules === true &&
          i.elastic_root_volume === true,
      ).length,
      all: allGPUInstances.length,
      persistent: allGPUInstances.filter((i) => i.stoppable === true).length,
      flexiblePorts: allGPUInstances.filter(
        (i) => i.can_modify_firewall_rules === true,
      ).length,
      flexibleStorage: allGPUInstances.filter(
        (i) => i.elastic_root_volume === true,
      ).length,
    };
  }, [allGPUInstances]);

  const handleModeChange = (mode: FilterMode) => {
    if (mode === "full") {
      setPersistentOnly(true);
      setFlexiblePortControlOnly(true);
      setFlexibleStorageOnly(true);
    } else if (mode === "all") {
      setPersistentOnly(false);
      setFlexiblePortControlOnly(false);
      setFlexibleStorageOnly(false);
    }
  };

  const getDisplayText = () => {
    switch (currentMode) {
      case "full":
        return "Only instances with full capabilities";
      case "all":
        return "All instances";
      case "custom":
        if (persistentOnly) {
          if (flexiblePortControlOnly) {
            return "Only stoppable instances with flexible ports";
          }
          if (flexibleStorageOnly) {
            return "Only stoppable instances with flexible storage";
          }
          return "Only stoppable instances";
        }
        if (flexiblePortControlOnly) {
          if (flexibleStorageOnly) {
            return "Only instances with flexible ports and storage";
          }
          return "Only instances with flexible ports";
        }
        if (flexibleStorageOnly) {
          return "Only instances with flexible storage";
        }
        return "Custom";
    }
  };

  return (
    <Popover>
      <div className="relative inline-flex">
        <PopoverTrigger asChild>
          <FilterCard
            icon={<CogIcon className="w-4 h-4" />}
            title={getDisplayText()}
            filterApplied={currentMode !== "all"}
            hasTrailingAction={currentMode !== "all"}
          />
        </PopoverTrigger>
        {currentMode !== "all" && (
          <FilterRemoveButton
            filterName="capabilities"
            onRemove={() => handleModeChange("all")}
          />
        )}
      </div>

      <PopoverContent className="w-[calc(100vw-2rem)] max-w-[400px] overflow-y-auto p-4">
        <div className="mb-4 flex w-full flex-col">
          {/* Header */}
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-white mb-1">
              Instance Capabilities
            </h3>
            <p className="text-xs text-gray-400">
              Filter instances by their features
            </p>
          </div>

          {/* Quick Shortcuts */}
          <div className="space-y-2 mb-4">
            <QuickShortcutButton
              label="All Instances"
              count={counts.all}
              isActive={currentMode === "all"}
              onClick={() => handleModeChange("all")}
            />
            <QuickShortcutButton
              label="Full Capabilities"
              count={counts.full}
              isActive={currentMode === "full"}
              onClick={() => handleModeChange("full")}
            />
          </div>

          {/* Divider */}
          <div className="border-t border-zinc-700 my-4" />

          {/* Custom Options */}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
              Custom Filters
            </p>

            <CapabilitiesFilterItem
              label="Persistent Instances"
              description="Can stop & start without data loss"
              tooltipText="Instances that can be stopped and started without losing data. Ideal for long-running projects."
              count={counts.persistent}
              checked={persistentOnly}
              onCheckedChange={setPersistentOnly}
            />

            <CapabilitiesFilterItem
              label="Flexible Port Control"
              description="Configure firewall & ports anytime"
              tooltipText="Ability to configure custom firewall rules and open/close ports anytime. Essential for web servers or custom services."
              count={counts.flexiblePorts}
              checked={flexiblePortControlOnly}
              onCheckedChange={setFlexiblePortControlOnly}
            />

            <CapabilitiesFilterItem
              label="Flexible Storage"
              description="Configure storage amount freely"
              tooltipText="Configure any amount of storage when creating the instance. Not limited to preset storage sizes."
              count={counts.flexibleStorage}
              checked={flexibleStorageOnly}
              onCheckedChange={setFlexibleStorageOnly}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CapabilitiesFilter;
